import { useCallback, useEffect, useMemo, useState } from 'react'
import { CategoryButton } from '../ui/CategoryButton'
import { Screen } from '../ui/Screen'
import { Snackbar } from '../ui/Snackbar'
import { StartSheet } from '../ui/StartSheet'
import { StaleBanner } from '../ui/StaleBanner'
import { useTick } from '../ui/useTick'
import { useKnownTags } from '../ui/useBlockActions'
import { useWorkspace } from '../data/workspace'
import {
  MISTAP_MS,
  planMoveBoundary,
  planResize,
  planSplit,
  planStop,
  planSwitch,
  planUndo,
  type BlockWrite,
} from '../data/blockPlan'
import { commitPlan, subscribeBlocksInRange } from '../data/blocks'
import { normaliseLayout } from '../data/layout'
import { recordTagUse } from '../data/tags'
import { newId } from '../lib/id'
import { formatClock, formatElapsed } from '../lib/format'
import { snapMs } from '../lib/timeline'
import { dayRange, todayKey } from '../lib/time'
import type { Block, Category } from '../data/types'

interface UndoState {
  before: Block[]
  writes: BlockWrite[]
  label: string
}

export function NowPage() {
  const { uid, taxonomy, categories, settings, openBlock } = useWorkspace()
  const now = useTick()
  const [error, setError] = useState<string | null>(null)
  const [undo, setUndo] = useState<UndoState | null>(null)
  const [sheetFor, setSheetFor] = useState<Category | null>(null)
  const [staleDismissedAt, setStaleDismissedAt] = useState<number | null>(null)

  const layout = normaliseLayout(taxonomy?.layout)
  const knownTags = useKnownTags(uid)
  const running = openBlock ? categories.get(openBlock.categoryId) : undefined
  const today = todayKey(settings.dayStartHour)
  const recentBlocks = useRecentBlocks(uid, today, settings.dayStartHour)
  // The block the running one was switched from: backfilling moves their shared
  // boundary, so it has to grow by exactly what the running block gains.
  const previous = useMemo(
    () => (openBlock ? (recentBlocks.find((block) => block.end === openBlock.start) ?? null) : null),
    [recentBlocks, openBlock],
  )

  const commit = useCallback(
    async (plan: ReturnType<typeof planSwitch>, label: string, before: Block[]) => {
      if (!plan.ok) {
        setError(plan.message)
        return
      }
      setError(null)
      if (plan.writes.length === 0) return
      setUndo({ before, writes: plan.writes, label })
      try {
        await commitPlan(uid, plan)
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Could not save that.')
      }
    },
    [uid],
  )

  const stop = useCallback(
    (at: number = Date.now()) => {
      if (!openBlock) return
      setStaleDismissedAt(null)
      void commit(planStop(openBlock, at), 'Stopped', [openBlock])
    },
    [commit, openBlock],
  )

  const switchTo = useCallback(
    (categoryId: string, extras: { note?: string | null; tags?: string[]; at?: number } = {}) => {
      const category = categories.get(categoryId)
      const now = Date.now()

      // Tapping what is already running stops it. Nothing has to be running —
      // untracked time is data, not a gap to be filled — and the button that is
      // lit is the obvious place to reach for to end it.
      const plain = extras.at === undefined && !extras.note && !extras.tags?.length
      if (openBlock?.categoryId === categoryId && plain) {
        // Except immediately after starting it: that is a double-tap, not a
        // change of mind, and stopping would leave a sliver behind.
        if (now - openBlock.start >= MISTAP_MS) stop(now)
        return
      }

      const plan = planSwitch({
        open: openBlock,
        at: extras.at ?? now,
        categoryId,
        newId: newId(),
        source: extras.at ? 'backfill' : 'tap',
        note: extras.note ?? null,
        tags: extras.tags ?? [],
        blocks: recentBlocks,
      })
      setStaleDismissedAt(null)
      // Undo restores from everything the plan could have touched, not just the
      // running block: a switch can also cut a block logged ahead of now.
      void commit(plan, `Switched to ${category?.shortLabel ?? categoryId}`, recentBlocks)
      if (extras.tags?.length) void recordTagUse(uid, extras.tags)
    },
    [categories, commit, openBlock, recentBlocks, stop, uid],
  )

  /**
   * "I started this earlier" — moves the boundary back, which means the block
   * before it grows to match rather than a gap opening up. Repeatable: each
   * press takes another few minutes off the start.
   */
  function backfill(minutes: number) {
    if (!openBlock) return
    const at = snapMs(openBlock.start - minutes * 60_000)
    const plan = previous
      ? planMoveBoundary(previous, openBlock, at)
      : planResize(openBlock, { start: at }, { previous: null, next: null })
    void commit(
      plan,
      `Started ${minutes}m earlier`,
      previous ? [previous, openBlock] : [openBlock],
    )
  }

  function revert() {
    if (!undo) return
    const plan = planUndo(undo.before, undo.writes)
    setUndo(null)
    if (!plan.ok) {
      setError(plan.message)
      return
    }
    void commitPlan(uid, plan).catch(() => setError('Could not undo that.'))
  }

  const staleAfter = settings.staleThresholdMinutes * 60_000
  const stale =
    openBlock && now - openBlock.start > staleAfter && staleDismissedAt !== openBlock.start

  return (
    <Screen>
      <section className="mb-2 rounded-2xl border border-line bg-surface px-4 py-3" aria-live="polite">
        {openBlock && running ? (
          <div className="flex items-center gap-3">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: running.color }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] leading-tight font-medium text-text">{running.label}</p>
              <p className="text-[12px] leading-tight text-faint tnum">
                since {formatClock(new Date(openBlock.start))}
              </p>
            </div>
            <p className="text-[30px] leading-none font-semibold tracking-tight tnum">
              {formatElapsed(now - openBlock.start)}
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="size-2.5 shrink-0 rounded-full border border-line-strong" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] leading-tight font-medium text-dim">Not tracking</p>
              <p className="text-[12px] leading-tight text-faint">Tap a mode to start.</p>
            </div>
          </div>
        )}

        {openBlock ? (
          <div className="flex items-center gap-1 pt-2.5">
            <span className="pr-1 text-[11px] font-medium tracking-wide text-faint uppercase">
              started earlier
            </span>
            {[5, 15, 30].map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => backfill(minutes)}
                title={`Move the start back ${minutes} minutes`}
                className="rounded-md border border-line px-1.5 py-1 text-[12px] font-medium text-dim tnum transition-colors duration-150 hover:border-line-strong hover:text-text"
              >
                −{minutes}m
              </button>
            ))}
            <button
              type="button"
              onClick={() => stop()}
              title="Stop tracking. Untracked time is data too."
              className="ml-auto rounded-md border border-line px-2.5 py-1 text-[12px] font-medium text-dim transition-colors duration-150 hover:border-danger/40 hover:text-danger"
            >
              Stop
            </button>
          </div>
        ) : null}
      </section>

      {stale && openBlock ? (
        <StaleBanner
          block={openBlock}
          category={running}
          now={now}
          onSplit={(at) =>
            void commit(planSplit(openBlock, at, newId()), 'Split', [openBlock])
          }
          onEnd={(at) => void commit(planStop(openBlock, at), 'Ended', [openBlock])}
          onDismiss={() => setStaleDismissedAt(openBlock.start)}
        />
      ) : null}

      {error ? (
        <p className="mb-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-[13px] text-danger">
          {error}
        </p>
      ) : null}

      <div
        className="grid flex-1 gap-2"
        style={{
          gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
        }}
      >
        {layout.cells.map((cell) => (
          <div
            key={cell.id}
            className="flex flex-col gap-2"
            style={{
              gridColumn: `${cell.column + 1} / span ${cell.colSpan}`,
              gridRow: `${cell.row + 1} / span ${cell.rowSpan}`,
            }}
          >
            {cell.categoryIds.map((categoryId) => {
              const category = categories.get(categoryId)
              if (!category) return null
              return (
                <CategoryButton
                  key={categoryId}
                  category={category}
                  running={openBlock?.categoryId === categoryId}
                  half={cell.categoryIds.length > 1}
                  onSelect={switchTo}
                  onHold={() => setSheetFor(category)}
                />
              )
            })}
          </div>
        ))}
      </div>

      {sheetFor ? (
        <StartSheet
          category={sheetFor}
          knownTags={knownTags}
          onStart={(details) => {
            const category = sheetFor
            setSheetFor(null)
            switchTo(category.id, { note: details.note, tags: details.tags })
          }}
          onClose={() => setSheetFor(null)}
        />
      ) : null}

      {undo ? (
        <Snackbar
          message={undo.label}
          actionLabel="Undo"
          onAction={revert}
          onDismiss={() => setUndo(null)}
        />
      ) : null}
    </Screen>
  )
}

/** Recent blocks, for finding the running block's neighbour when backfilling. */
function useRecentBlocks(uid: string, day: string, dayStartHour: number): Block[] {
  const [blocks, setBlocks] = useState<Block[]>([])
  const range = useMemo(() => dayRange(day, dayStartHour), [day, dayStartHour])

  useEffect(() => {
    const from = new Date(range.start.getTime() - 21 * 24 * 60 * 60 * 1000)
    return subscribeBlocksInRange(uid, from, range.end, setBlocks)
  }, [uid, range])

  return blocks
}

